import {
  Component,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  AlertService,
  AlertType,
  FieldType,
  GenericModalComponent,
  LoaderService,
  ModalFieldConfig,
  ModalMode,
} from '@solcre-org/core-ui';
import { BookingModel } from '../../../shared/models/booking.model';
import { BookingAttachmentModel } from '../../../shared/models/booking-attachment.model';
import { forkJoin, Observable } from 'rxjs';
import { BookingService } from '../../../shared/services/booking.service';

@Component({
  selector: 'app-booking-attachments-modal',
  imports: [GenericModalComponent, TranslateModule],
  templateUrl: './booking-attachments-modal.html',
})
export class BookingAttachmentsModal {
  modalmodes = ModalMode;

  private alertService = inject(AlertService);
  private loaderService = inject(LoaderService);
  private bookingService = inject(BookingService);
  private translateService = inject(TranslateService);

  booking = input.required<BookingModel>();
  onClose = output<void>();
  onSuccessSave = output<void>();
  model = signal<BookingAttachmentModel>(new BookingAttachmentModel());
  modelFactory = (data: any) => new BookingAttachmentModel().fromJSON(data);

  modalFields = computed<ModalFieldConfig<BookingAttachmentModel>[]>(() => [
    {
      key: 'files',
      label: 'bookings.attachments',
      placeholder: 'bookings.attachmentsPlaceholder',
      type: FieldType.FILE,
      accept:
        'application/pdf, application/vnd.ms-excel, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, image/jpeg, image/png, text/plain, application/msword, application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      acceptString: '.pdf, .xls, .xlsx, .jpg, .jpeg, .png, .txt, .doc, .docx',
      multiple: true,
      readonly: false,
    },
  ]);

  constructor() {
    effect(() => {
      this.model.set(new BookingAttachmentModel());
    });
  }

  onSaveEmit(data: BookingAttachmentModel) {
    const files: File[] = data.files ?? [];

    //Skip no files
    if (!files || !files.length) {
      this.onClose.emit();
      return;
    }

    const uploads: Observable<any>[] = files.map((file: File) => {
      return this.bookingService.uploadBookingAttachment(
        this.booking().id,
        file
      );
    });

    this.loaderService.showLoader();
    forkJoin(uploads).subscribe({
      next: (res) => {
        this.onClose.emit();
        this.loaderService.hideLoader();
        this.alertService.show({
          type: AlertType.SUCCESS,
          message: this.translateService.instant(
            'bookings.uploadAttachmentsSuccess'
          ),
          duration: 5000,
          inBottom: true,
        });
        this.onSuccessSave.emit();
      },
      error: (error: any) => {
        this.loaderService.hideLoader();
        this.alertService.show({
          type: AlertType.ERROR,
          message: this.translateService.instant(
            'bookings.uploadAttachmentsError'
          ),
          duration: 5000,
          inBottom: true,
        });
      },
    });
  }

  onCloseEmit(): void {
    this.onClose.emit();
  }
}
